import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { UsersRound, Cloud, ArrowUpRight } from "lucide-react";
import SectionHeading from "../../components/ui/SectionHeading";
import { fadeUp } from "../../utils/animations";
import { useNavigate } from "react-router-dom";

const journey = [
  {
    step: "Tell Us Your Goal",
    copy: "Share where you are and where you want to be. Use our course finder, talk to a training advisor, or start with one of our curated career pathways.",
    emoji: "🧭",
  },
  {
    step: "Pick Your Format & Date",
    copy: "Choose 1-on-1, Public Batch, or Flexi. Select dates from guaranteed schedules that fit your life. Lock in your spot with flexible payment options.",
    emoji: "📋",
  },
  {
    step: "Train with a Real Expert",
    copy: "A vendor-certified instructor teaches you live. Hands-on labs mirror real enterprise environments. Sessions are recorded so you can review later.",
    emoji: "🎓",
  },
  {
    step: "Certify & Advance",
    copy: "Pass your exam with dedicated prep and practice tests. Join 1M+ certified professionals who used Koenig to land promotions and salary increases.",
    emoji: "🚀",
  },
];

const ACTIVE_INDEX = 2; // "Train with a Real Expert" — highlighted, exactly like the screenshot

const enterprise = [
  {
    title: "Enterprise Upskilling",
    copy: "Custom learning paths, skill reports, cohorts, and measurable ROI.",
    icon: UsersRound,
  },
  {
    title: "Digital Transformation",
    copy: "Cloud, managed services, consulting, analytics, and implementation support.",
    icon: Cloud,
  },
];

export default function EcosystemSection() {
    const navigate = useNavigate();

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        bgcolor: "white",
        py: { xs: 10, md: 14 },
        overflow: "hidden",
      }}
    >
      {/* soft ambient background glow for a premium feel */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 500,
          background:
            "radial-gradient(closest-side, rgba(37,99,235,0.07), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div className="section-shell" style={{ position: "relative" }}>
        <Box sx={{ textAlign: "center", mx: "auto" }}>
          <SectionHeading
            align="center"
            eyebrow="Why NetLabs"
            title="Learn, practice, certify, get hired, and upskill teams from one premium platform."
            copy="The experience is designed like a global EdTech and enterprise training platform, not a traditional training institute website."
          />
        </Box>

        {/* ============ Learner Journey — How It Works ============ */}
        <Box sx={{ mt: { xs: 8, md: 10 } }}>
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: 800,
              letterSpacing: "-0.01em",
              fontSize: { xs: "1.5rem", md: "1.9rem" },
              color: "#0B1530",
              mb: 1,
            }}
          >
            How It <Box component="span" sx={{ color: "primary.main" }}>Works</Box>
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              maxWidth: 560,
              mx: "auto",
              mb: { xs: 6, md: 8 },
              fontSize: "0.95rem",
              lineHeight: 1.7,
            }}
          >
            From choosing your path to getting certified — four steps that have
            worked for over a million professionals.
          </Typography>

          {/* horizontal scroll wrapper keeps all 4 steps on ONE row at every screen size */}
          <Box sx={{ overflowX: { xs: "auto", md: "visible" }, pb: { xs: 3, md: 0 } }}>
            <Box
              sx={{
                position: "relative",
                minWidth: { xs: 880, md: "auto" },
              }}
            >
              {/* connecting line through icon centers */}
              <Box
                sx={{
                  position: "absolute",
                  top: 34,
                  left: "12.5%",
                  right: "12.5%",
                  height: "2px",
                  bgcolor: "rgba(37,99,235,0.18)",
                  zIndex: 0,
                }}
              />

              <Grid
                container
                wrap="nowrap"
                spacing={{ xs: 2.5, md: 3 }}
                sx={{ position: "relative", zIndex: 1 }}
              >
                {journey.map(({ step, copy, emoji }, i) => {
                  const isActive = i === ACTIVE_INDEX;
                  return (
                    <Grid item xs={3} key={step} sx={{ minWidth: 0 }}>
                      <motion.div {...fadeUp(i * 0.08)} style={{ height: "100%" }}>
                        <Box
                          className={`journey-node${isActive ? " journey-node--active" : ""}`}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                            height: "100%",
                          }}
                        >
                          {/* icon circle with number badge */}
                          <Box
                            sx={{
                              position: "relative",
                              flexShrink: 0,
                              width: 68,
                              height: 68,
                              mb: 2.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: isActive ? 68 : 60,
                                height: isActive ? 68 : 60,
                                mx: "auto",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: isActive ? "primary.main" : "#EFF4FF",
                                border: isActive
                                  ? "none"
                                  : "1px solid rgba(37,99,235,0.18)",
                                boxShadow: isActive
                                  ? "0 0 0 6px rgba(37,99,235,0.14), 0 14px 28px -12px rgba(37,99,235,0.55)"
                                  : "0 10px 24px -14px rgba(37,99,235,0.35)",
                                transition: "all 0.3s ease",
                                ".journey-node:hover &": {
                                  bgcolor: "primary.main",
                                  transform: "scale(1.06)",
                                },
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: isActive ? 30 : 26,
                                  lineHeight: 1,
                                }}
                              >
                                {emoji}
                              </Typography>
                            </Box>

                            {/* number badge */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: -4,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                bgcolor: "#0B1530",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 800,
                                border: "2px solid white",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                              }}
                            >
                              {i + 1}
                            </Box>
                          </Box>

                          {/* card */}
                          <Box
                            sx={{
                              width: "100%",
                              flex: 1,
                              p: { xs: 2.5, md: 3 },
                              borderRadius: "18px",
                              border: isActive
                                ? "1.5px solid #2563EB"
                                : "1px solid rgba(15,23,42,0.08)",
                              bgcolor: isActive ? "#F6F9FF" : "white",
                              boxShadow: isActive
                                ? "0 22px 44px -18px rgba(37,99,235,0.45)"
                                : "0 2px 10px rgba(15,23,42,0.04)",
                              textAlign: "center",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transition:
                                "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease, background-color 0.28s ease",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 24px 46px -20px rgba(37,99,235,0.4)",
                                borderColor: "rgba(37,99,235,0.5)",
                                bgcolor: "#F6F9FF",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                color: "primary.main",
                                letterSpacing: "0.08em",
                                mb: 0.75,
                                textAlign: "center",
                              }}
                            >
                              STEP {String(i + 1).padStart(2, "0")}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              sx={{
                                letterSpacing: "-0.01em",
                                textAlign: "center",
                                fontSize: "0.98rem",
                              }}
                            >
                              {step}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              lineHeight={1.7}
                              sx={{
                                maxWidth: 240,
                                mx: "auto",
                                textAlign: "center",
                                fontSize: "0.82rem",
                              }}
                            >
                              {copy}
                            </Typography>

                            {/* progress dots */}
                            <Box sx={{ display: "flex", gap: 0.6, mt: 2 }}>
                              {[0, 1, 2, 3].map((d) => (
                                <Box
                                  key={d}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor:
                                      d <= i ? "primary.main" : "rgba(15,23,42,0.15)",
                                    transition: "background-color 0.3s ease",
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* ============ Enterprise track — centered on screen ============ */}
        <Box sx={{ mt: { xs: 10, md: 12 } }}>
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: 800,
              letterSpacing: "-0.01em",
              fontSize: { xs: "1.15rem", md: "1.4rem" },
              color: "text.primary",
              mb: { xs: 5, md: 6 },
            }}
          >
            For Organizations
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "stretch",
              gap: { xs: 3, md: 4 },
              maxWidth: 980,
              mx: "auto",
            }}
          >
            {enterprise.map(({ title, copy, icon: Icon }, i) => (
              <motion.div
                {...fadeUp(i * 0.08)}
                style={{
                  flex: "1 1 420px",
                  maxWidth: 460,
                  display: "flex",
                }}
                key={title}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 2.5,
                    p: { xs: 3, md: 4 },
                    borderRadius: "20px",
                    border: "1px solid rgba(15,23,42,0.08)",
                    bgcolor: "white",
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
                    transition:
                      "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 24px 48px -20px rgba(37,99,235,0.35)",
                      borderColor: "rgba(37,99,235,0.35)",
                    },
                    "&:hover .ecosystem-arrow": { transform: "translate(2px, -2px)" },
                  }}
                >

                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                      color: "white",
                      boxShadow: "0 12px 24px -12px rgba(37,99,235,0.6)",
                    }}
                  >
                    <Icon size={24} strokeWidth={1.8} />
                  </Box>

                  <Box sx={{ position: "relative" }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ letterSpacing: "-0.01em", mb: 1, textAlign: "center" }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      lineHeight={1.75}
                      sx={{ maxWidth: 380, mx: "auto", textAlign: "center" }}
                    >
                      {copy}
                    </Typography>
                  </Box>

                 <Box
                      component="button"
                      onClick={() => navigate("/business")}
                      sx={{
                        all: "unset",
                        cursor: "pointer",
                        mt: "auto",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        px: 2.5,
                        py: 1,
                        borderRadius: "999px",
                        border: "1px solid rgba(37,99,235,0.28)",
                        color: "primary.main",
                        fontWeight: 700,
                        fontSize: 13.5,
                        transition: "background-color 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          bgcolor: "#EFF4FF",
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      Learn more
                      <ArrowUpRight
                        size={16}
                        className="ecosystem-arrow"
                        style={{ transition: "transform 0.2s ease" }}
                      />
                    </Box>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Box>
      </div>
    </Box>
  );
}